import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Lock, Crown, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SubscriptionTier } from '@/types/subscription';

interface SubscriptionRestrictionProps {
  requiredTier: SubscriptionTier;
  feature: string;
  description?: string;
  showUpgrade?: boolean;
}

export const SubscriptionRestriction: React.FC<SubscriptionRestrictionProps> = ({
  requiredTier,
  feature,
  description,
  showUpgrade = true,
}) => {
  const navigate = useNavigate();

  const getTierInfo = (tier: SubscriptionTier) => {
    switch (tier) {
      case 'premium':
        return {
          icon: <Star className="h-4 w-4" />,
          label: 'Premium',
          color: 'bg-primary text-primary-foreground',
          price: '59€/mois',
        };
      case 'enterprise':
        return {
          icon: <Crown className="h-4 w-4" />,
          label: 'Enterprise',
          color: 'bg-gradient-to-r from-primary to-primary-glow text-primary-foreground',
          price: '99€/mois',
        };
      default:
        return {
          icon: <Lock className="h-4 w-4" />,
          label: 'Basic',
          color: 'bg-muted text-muted-foreground',
          price: '29€/mois',
        };
    }
  };

  const tierInfo = getTierInfo(requiredTier);

  return (
    <Card className="border border-primary/20 bg-muted/50">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-2">
          <Badge className={tierInfo.color}>
            {tierInfo.icon}
            <span className="ml-1">{tierInfo.label}</span>
          </Badge>
        </div>
        <CardTitle className="flex items-center justify-center gap-2">
          <Lock className="h-5 w-5 text-muted-foreground" />
          Fonctionnalité Restreinte
        </CardTitle>
        <CardDescription>
          {description || `Cette fonctionnalité "${feature}" nécessite un abonnement ${tierInfo.label}.`}
        </CardDescription>
      </CardHeader>
      
      {showUpgrade && (
        <CardContent className="text-center">
          <p className="text-sm text-muted-foreground mb-4">
            Passez à {tierInfo.label} pour débloquer {feature} et bien plus.
          </p>
          <Button 
            onClick={() => navigate('/subscription')}
            className="w-full"
          >
            Passer à {tierInfo.label} - {tierInfo.price}
          </Button>
        </CardContent>
      )}
    </Card>
  );
};

interface RestrictedContentProps {
  requiredTier: SubscriptionTier;
  hasAccess: boolean;
  feature: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const RestrictedContent: React.FC<RestrictedContentProps> = ({
  requiredTier,
  hasAccess,
  feature,
  children,
  fallback,
}) => {
  if (hasAccess) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  return (
    <SubscriptionRestriction
      requiredTier={requiredTier}
      feature={feature}
    />
  );
};